import React, { useMemo } from "react";
import clsx from "clsx";
import { Col, Modal, Row } from "react-bootstrap";

import { CloseButton } from "components";
import { getSourceFileName } from "mappers";
import {
	ResidualsConflicts,
	ResidualsConflictTypes,
	SourceFileType,
} from "types";

import { ReactComponent as WarningIcon } from "static/images/warning-alert.svg";
import styles from "./residualsModal.module.scss";

const LabeledValue = ({ label, value, classNameValue = "" }) => (
	<Row className={styles.row}>
		<Col className={styles.label}>
			<span className={styles.innerContent}>{label}:</span>
		</Col>
		<Col className={`${styles.value} ${classNameValue}`}>
			<div className={styles.innerContent}>{value}</div>
		</Col>
	</Row>
);

type ResidualsConflictsModalProps = {
	show: boolean;
	onHide: () => void;
	type: ResidualsConflictTypes;
	conflicts: ResidualsConflicts;
	fileInfo: SourceFileType;
};

const ResidualsConflictsModal = ({
	show,
	onHide,
	type,
	conflicts,
	fileInfo,
}: ResidualsConflictsModalProps) => {
	const labels = useMemo(() => {
		switch (type) {
			case "duplicate":
				return {
					title: "Duplicates",
					fieldName: "MID",
					fieldPosition: "Line number",
				};
			case "newDataField":
				return {
					title: "New Data Fields",
					fieldName: "Column header",
					fieldPosition: "Position/number",
				};
			case "newRevenueExpenseItem":
				return {
					title: "New Revenue & Expense Items",
					fieldName: "Fee Name",
				};
			default:
				return null;
		}
	}, [type]);

	const isDuplicateType = type === "duplicate";
	const isNewDataFieldType = type === "newDataField";
	const isNewRevenueExpenseItemType = type === "newRevenueExpenseItem";

	return (
		<Modal
			show={show}
			onHide={onHide}
			centered
			animation={false}
			size="lg"
			className={styles.centeredLabelsModal}
		>
			<Modal.Header>
				<Modal.Title as="h6" className="text-center">
					{labels?.title}
				</Modal.Title>
				<CloseButton onClick={onHide} />
			</Modal.Header>
			<Modal.Body className={styles.bodyInner}>
				<LabeledValue label="File ID" value={fileInfo.number} />
				<LabeledValue
					label="File Name"
					value={getSourceFileName(fileInfo.name)}
					classNameValue="text-primary text-uppercase"
				/>
				{isNewDataFieldType && (
					<>
						<LabeledValue
							label="Source Data Tables"
							value={fileInfo.source_data_tables?.join(", ")}
						/>
						<div
							className={clsx(
								styles.conflictsTable,
								"light-grey-text",
							)}
						>
							<Row className={styles.tableRow}>
								<Col className={styles.tableHeaderLeft}>
									<span className={styles.innerContent}>
										Column header
									</span>
								</Col>
								<Col className={styles.tableHeaderRight}>
									<span className={styles.innerContent}>
										Position/number
									</span>
								</Col>
							</Row>
							{conflicts?.items?.map(c => {
								return (
									<Row
										className={clsx(
											styles.tableRow,
											"text-warning",
										)}
									>
										<Col className={styles.labelWarning}>
											<div
												className={styles.innerContent}
											>
												{c.field_name}
												<WarningIcon
													className={
														styles.warningIcon
													}
												/>
											</div>
										</Col>
										<Col className={styles.value}>
											<span
												className={styles.innerContent}
											>
												{c.field_position}
											</span>
										</Col>
									</Row>
								);
							})}
						</div>
					</>
				)}
				{isDuplicateType && (
					<div
						className={clsx(
							styles.conflictsTable,
							"light-grey-text",
						)}
					>
						<Row className={styles.tableRow}>
							<Col className={styles.tableHeaderLeft}>
								<span className={styles.innerContent}>
									{labels?.fieldName}
								</span>
							</Col>
							<Col className={styles.tableHeaderRight}>
								<span className={styles.innerContent}>
									{labels?.fieldPosition}
								</span>
							</Col>
						</Row>
						{conflicts?.items?.map((item, index) => (
							<Row
								className={clsx(
									styles.tableRow,
									"text-warning",
								)}
								key={index}
							>
								<Col className={styles.labelWarning}>
									<div className={styles.innerContent}>
										<div>
											{isNewDataFieldType
												? item.field_name
												: item.field_value}
										</div>
										<WarningIcon
											className={styles.warningIcon}
										/>
									</div>
								</Col>
								<Col className={styles.value}>
									<span className={styles.innerContent}>
										{item.field_position}
									</span>
								</Col>
							</Row>
						))}
					</div>
				)}
				{isNewRevenueExpenseItemType && (
					<div>
						{conflicts?.items?.map((item, index) => (
							<Row className={styles.row} key={index}>
								<Col className={styles.label}>
									{index === 0 && (
										<span className={styles.innerContent}>
											{labels?.fieldName}:
										</span>
									)}
								</Col>
								<Col
									className={`${styles.value} ${styles.textWarning}`}
								>
									<div className={styles.innerContent}>
										<div>{item.field_value}</div>
										<WarningIcon
											className={styles.warningIcon}
										/>
									</div>
								</Col>
							</Row>
						))}
					</div>
				)}
			</Modal.Body>
		</Modal>
	);
};

export default ResidualsConflictsModal;
